import { Back, Power0, Tween, TweenMax } from 'gsap';
import { getGameSize } from '../../../Config/Levels';
import Core, { GameOrientation } from '../../../Core/Core';
import { FocusManager } from '../../../Managers/FocusManager';
import Point = PIXI.Point;
import ObservablePoint = PIXI.ObservablePoint;
import DEG_TO_RAD = PIXI.DEG_TO_RAD;
import { IFBFriend } from '../../../SDK/SDKfb';
import { BoosterId } from '../../../State/reducers/boosters';
import { Animation } from '../../Animations/Animation';
import { ITweenable } from '../../PopUps/ITweenable';
import { UIButton } from '../../Shared/Buttons/UIButton';
import { HighlightEffect } from '../../Shared/HighlightEffect';
import { PlayerAvatar } from '../../Shared/PlayerAvatar';
import { BoosterButton } from '../BottomUI/BoosterButton';

enum Events {
    Trigger = 'Ask Friend',
}

export class BoosterParachute extends PIXI.Container implements ITweenable {
    public static readonly events = Events;
    protected tweeningOut: boolean;

    private readonly buttonPosition: Point | ObservablePoint;
    private readonly boosterButton: BoosterButton;
    private idleTween: Tween;
    private tweenInAnimation: Animation;

    constructor(friend: IFBFriend, boosterId: BoosterId, buttonPostion: Point | ObservablePoint) {
        super();
        const frame = new PIXI.Sprite(PIXI.utils.TextureCache['gameui/helper-avatar-frame']);
        frame.anchor.set(0.5);

        const playerAvatar = new PlayerAvatar(
            friend.avatar,
            frame.width * 0.56,
            frame.height * 0.56
        );
        playerAvatar.x -= playerAvatar.width * 0.05;
        playerAvatar.y += frame.height * 0.08;
        const boosterButton = (this.boosterButton = new BoosterButton(boosterId, false));
        boosterButton.interactive = false;
        boosterButton.x -= boosterButton.height * 0.02;
        boosterButton.y += frame.height / 2 + boosterButton.height * 0.05;
        boosterButton.scale.set(0.8);
        boosterButton.originalScale.set(boosterButton.scale.x, boosterButton.scale.y);

        const highlight = new HighlightEffect({ scale: 0.8 });
        highlight.position = boosterButton.position;
        highlight.appear();
        //@ts-ignore
        this.addChild(highlight, boosterButton);

        this.buttonPosition = buttonPostion;
        this.addChild(playerAvatar, frame);
    }

    public async tweenIn(): Promise<void> {
        const { width, height, buttonPosition, boosterButton } = this;
        const targetPosition = new Point(buttonPosition.x, buttonPosition.y - 1.1 * height);
        const isPortrait = Core.orientation === GameOrientation.PORTRAIT;
        const startingPosition = isPortrait
            ? new Point(-width, buttonPosition.y - 2 * height)
            : new Point(targetPosition.x, -height);
        const animation = (this.tweenInAnimation = new Animation({}));
        animation.add(
            TweenMax.fromTo(
                this,
                1,
                {
                    x: startingPosition.x,
                },
                {
                    x: targetPosition.x,
                }
            )
        );
        animation.addToLast(
            TweenMax.fromTo(
                this,
                2,
                {
                    y: startingPosition.y,
                },
                {
                    y: targetPosition.y,
                }
            )
        );
        animation.addToLast(
            TweenMax.fromTo(
                this,
                2.5,
                {
                    rotation: 20 * DEG_TO_RAD,
                },
                {
                    rotation: 0,
                    ease: Back.easeOut.config(2),
                }
            )
        );

        await animation.play();
        if (this.tweeningOut) {
            return;
        }
        this.idleTween = TweenMax.to(this, 1, {
            y: targetPosition.y - height * 0.02,
            yoyo: true,
            ease: Power0.easeNone,
            repeat: -1,
        });
        boosterButton.interactive = true;
        boosterButton.on(UIButton.events.Trigger, () => {
            this.emit(BoosterParachute.events.Trigger);
        });
    }

    public async tweenOut(success: boolean): Promise<void> {
        this.killTweens();
        const { tweensToResume } = FocusManager.instance;
        tweensToResume.splice(tweensToResume.indexOf(this.idleTween), 1);
        const isPortrait = Core.orientation === GameOrientation.PORTRAIT;
        const animation = new Animation({});
        animation.add(() => this.boosterButton.setActive(success));
        if (success) {
            animation.addDelay(1000);
        }
        animation.add(
            TweenMax.to(this, 2, {
                x: isPortrait ? -this.width : this.x,
                y: !isPortrait ? getGameSize().gameHeight + this.height : this.y,
                rotation: isPortrait ? -20 * DEG_TO_RAD : 0,
                alpha: 0,
            })
        );
        animation.add(() => {
            this.destroy();
        });
        await animation.play();
    }

    public destroy(options?: PIXI.DestroyOptions | boolean): void {
        this.killTweens();
        return super.destroy();
    }

    private killTweens() {
        this.tweeningOut = true;
        if (this.idleTween) {
            this.idleTween.kill();
        }
        if (this.tweenInAnimation) {
            this.tweenInAnimation.destroy();
        }
    }
}
